import { useEffect, useState } from "react";
import { adminAudit, adminListUsers, adminSetRole, adminSetRoleByUsername, type AuditEntry } from "../api/endpoints";
import { useUserStore } from "../store/user";
import { confirmAction, notifyError } from "../lib/ui";

const ACTION_LABEL: Record<string, string> = {
  role_change: "🔑 Смена роли",
  product_create: "➕ Добавлен товар",
  product_update: "✏️ Правка товара",
  order_issue: "✅ Выдан заказ",
  order_cancel: "🚫 Отмена брони",
};

function AuditSection() {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && entries === null) adminAudit().then(setEntries).catch(notifyError);
  };

  return (
    <div style={{ marginTop: 24, background: "var(--secondary-bg)", borderRadius: 16, overflow: "hidden" }}>
      <button
        onClick={toggle}
        style={{ width: "100%", padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none", color: "var(--text)", cursor: "pointer" }}
      >
        <span className="micro">📋 Журнал действий</span>
        <span style={{ color: "var(--hint)", transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>›</span>
      </button>
      {open && (
        <div style={{ padding: "0 16px 12px" }}>
          {entries === null && <p style={{ color: "var(--hint)", fontSize: 13 }}>Загрузка…</p>}
          {entries && entries.length === 0 && <p style={{ color: "var(--hint)", fontSize: 13 }}>Пока пусто.</p>}
          {entries && entries.map((e) => (
            <div key={e.id} style={{ padding: "8px 0", borderTop: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span className="micro">{ACTION_LABEL[e.action] ?? e.action}</span>
                <span className="micro" style={{ color: "var(--hint)", whiteSpace: "nowrap" }}>
                  {new Date(e.created_at).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              {e.detail && <p style={{ margin: "2px 0 0", fontSize: 13 }}>{e.detail}</p>}
              <p className="micro" style={{ margin: "3px 0 0", color: "var(--hint)" }}>{e.actor_label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
import type { Role, User } from "../types";

const ALL_ROLES: { value: Role; label: string }[] = [
  { value: "manager", label: "Менеджер"  },
  { value: "owner",   label: "Владелец"  },
  { value: "admin",   label: "Админ"     },
];

// Роли, доступные для назначения владельцем (без admin)
const OWNER_ROLES: { value: Role; label: string }[] = [
  { value: "manager", label: "Менеджер" },
  { value: "owner",   label: "Владелец" },
];

const ROLE_COLOR: Record<Role, string> = {
  client:  "var(--hint)",
  manager: "#4a9eff",
  owner:   "var(--accent)",
  admin:   "#b06aff",
};

const ROLE_LABEL: Record<string, string> = {
  client: "Клиент", manager: "Менеджер", owner: "Владелец", admin: "Админ",
};

function UserRow({ u, myRole, onUpdated }: { u: User; myRole: Role; onUpdated: (u: User) => void }) {
  const [busy, setBusy] = useState(false);
  const me = useUserStore((s) => s.user);

  // Владелец не может трогать adminов
  const isProtected = u.role === "admin" && myRole !== "admin";
  // Какие роли можно назначить
  const availableRoles = myRole === "admin" ? ALL_ROLES : OWNER_ROLES;

  const who = u.username ? `@${u.username}` : (u.first_name ?? `ID ${u.telegram_id}`);

  const changeRole = async (role: Role) => {
    if (role === u.role) return;
    const ok = await confirmAction(
      `Назначить роль «${ROLE_LABEL[role] ?? role}» пользователю ${who}?`
    );
    if (!ok) return;
    setBusy(true);
    try { onUpdated(await adminSetRole(u.id, role)); }
    catch (e) { notifyError(e); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    const ok = await confirmAction(`Снять роль у ${who}? Станет клиентом.`);
    if (!ok) return;
    setBusy(true);
    try { onUpdated(await adminSetRole(u.id, "client")); }
    catch (e) { notifyError(e); }
    finally { setBusy(false); }
  };

  const isSelf = u.id === me?.id;
  const canRevoke = !isProtected && !isSelf && u.role !== "client";

  return (
    <div style={{
      background: "var(--secondary-bg)",
      borderRadius: 14,
      padding: "12px 14px",
      marginBottom: 10,
      opacity: busy ? 0.6 : 1,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <span style={{ fontWeight: 600 }}>
            {u.first_name ?? "—"}
            {isSelf && <span style={{ color: "var(--hint)", fontWeight: 400 }}> (я)</span>}
          </span>
          {u.username && (
            <span className="micro" style={{ color: "var(--hint)", marginLeft: 6 }}>@{u.username}</span>
          )}
          <div className="micro" style={{ color: "var(--hint)", marginTop: 3 }}>ID: {u.telegram_id}</div>
        </div>
        <span className="pill" style={{
          background: "var(--border)",
          color: ROLE_COLOR[u.role as Role] ?? "var(--text)",
        }}>
          {ROLE_LABEL[u.role] ?? u.role}
        </span>
      </div>

      {!isProtected && (
        <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
          {availableRoles.map((r) => (
            <button
              key={r.value}
              disabled={busy || r.value === u.role}
              onClick={() => changeRole(r.value)}
              style={{
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "4px 10px",
                fontSize: 12,
                cursor: r.value === u.role ? "default" : "pointer",
                background: r.value === u.role ? "var(--button)" : "var(--secondary-bg)",
                color: r.value === u.role ? "var(--button-text)" : "var(--text)",
                opacity: busy ? 0.5 : 1,
              }}
            >
              {r.label}
            </button>
          ))}
          {canRevoke && (
            <button
              disabled={busy}
              onClick={revoke}
              style={{
                border: "1px solid #b00020",
                borderRadius: 8,
                padding: "4px 10px",
                fontSize: 12,
                cursor: "pointer",
                background: "transparent",
                color: "#b00020",
                marginLeft: "auto",
              }}
            >
              Снять роль
            </button>
          )}
        </div>
      )}

      {isProtected && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--hint)" }}>
          Роль администратора защищена
        </p>
      )}
    </div>
  );
}

function AssignRoleBlock({ myRole, onAssigned }: { myRole: Role; onAssigned: (u: User) => void }) {
  const availableRoles = myRole === "admin" ? ALL_ROLES : OWNER_ROLES;
  const [username, setUsername] = useState("");
  const [role, setRole] = useState<Role>("manager");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const assign = async () => {
    const trimmed = username.trim().replace(/^@/, "");
    if (!trimmed) return;
    const roleLabel = availableRoles.find((r) => r.value === role)?.label ?? role;
    const ok = await confirmAction(`Назначить роль «${roleLabel}» пользователю @${trimmed}?`);
    if (!ok) return;
    setBusy(true);
    setResult(null);
    try {
      const updated = await adminSetRoleByUsername(trimmed, role);
      onAssigned(updated);
      setResult({ ok: true, text: `✓ ${updated.first_name ?? "@" + trimmed} → ${availableRoles.find((r) => r.value === role)?.label}` });
      setUsername("");
    } catch (e) {
      setResult({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: "var(--secondary-bg)", borderRadius: 16, padding: 16, marginBottom: 20 }}>
      <p className="section-label" style={{ margin: "0 0 12px" }}>Назначить роль</p>

      <div className="field">
        <label>Username</label>
        <input
          placeholder="@username"
          value={username}
          onChange={(e) => { setUsername(e.target.value); setResult(null); }}
          onKeyDown={(e) => e.key === "Enter" && assign()}
        />
      </div>

      <div className="field">
        <label>Роль</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {availableRoles.map((r) => (
            <button
              key={r.value}
              onClick={() => setRole(r.value)}
              style={{
                flex: 1,
                padding: "8px 0",
                borderRadius: 10,
                border: "1px solid",
                borderColor: role === r.value ? "var(--accent)" : "var(--border)",
                background: role === r.value ? "var(--accent)" : "var(--bg)",
                color: role === r.value ? "#fff" : "var(--text)",
                fontWeight: role === r.value ? 600 : 400,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <button className="btn" onClick={assign} disabled={busy || !username.trim()}>
        {busy ? "…" : "Назначить"}
      </button>

      {result && (
        <p style={{ margin: "10px 0 0", fontSize: 13, color: result.ok ? "#1a7f1a" : "#b00020" }}>
          {result.text}
        </p>
      )}
    </div>
  );
}

export default function AdminPage() {
  const myRole = useUserStore((s) => (s.viewAs ?? s.user?.role ?? "client")) as Role;
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminListUsers()
      .then(setUsers)
      .finally(() => setLoading(false));
  }, []);

  const handleUpdated = (updated: User) =>
    setUsers((prev) =>
      prev.some((u) => u.id === updated.id)
        ? prev.map((u) => (u.id === updated.id ? updated : u))
        : [...prev, updated]
    );

  return (
    <div className="container">
      <h1>Пользователи</h1>

      <AssignRoleBlock myRole={myRole} onAssigned={handleUpdated} />

      {loading && <p>Загрузка…</p>}

      {!loading && (
        <>
          <p className="micro" style={{ color: "var(--hint)", marginBottom: 16 }}>
            {users.length} пользователей
          </p>
          {users.map((u) => (
            <UserRow key={u.id} u={u} myRole={myRole} onUpdated={handleUpdated} />
          ))}
        </>
      )}

      <AuditSection />
    </div>
  );
}
